/**
 * @file api.ts
 * @brief TanStack Query hooks for Company Admin Reports.
 *
 * @references
 * - BACKEND/src/features/company/routes.js (/api/company/reports/*)
 *
 * @author DES
 * @date 27-07-2026
 */

import { useQuery, type UseQueryOptions } from "@tanstack/react-query";
import { apiGet } from "@/shared/api/client";
import { companyEndpoints } from "@/shared/api/endpoints";
import {
  AdoptionReportSchema,
  EnrollmentReportSchema,
  MeetingIntelReportSchema,
  ProductMixSchema,
  ScheduleOpsReportSchema,
  SpendByProductSchema,
  UsageByEmployeeSchema,
  type AdoptionReport,
  type CompanyReportRange,
  type EnrollmentReport,
  type MeetingIntelReport,
  type ProductMixRow,
  type ScheduleOpsReport,
  type SpendByProductRow,
  type UsageByEmployeeRow,
} from "./schemas";

/**
 * The viewer's IANA zone, sent with every windowed query so the backend groups
 * day/week/month buckets on the SAME calendar the window was cut on. Without
 * it the server truncates on its own zone (UTC) and a bucket can fall outside
 * the requested range — the defect found in the invoices daily-spend feed on
 * 24-08-2026. Omitting it keeps the old UTC behaviour.
 */
function viewerTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

function rangeQuery(range: CompanyReportRange): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(range)) {
    if (v !== undefined && v !== null && v !== "") usp.set(k, String(v));
  }
  usp.set("tz", viewerTimeZone());
  const s = usp.toString();
  return s ? `?${s}` : "";
}

export const companyReportsKeys = {
  all: ["company", "reports"] as const,
  usageByEmployee: (range: CompanyReportRange) =>
    ["company", "reports", "usage-by-employee", range] as const,
  spendByProduct: (range: CompanyReportRange) =>
    ["company", "reports", "spend-by-product", range] as const,
  meetingIntel: (range: CompanyReportRange) =>
    ["company", "reports", "meeting-intel", range] as const,
  productMix: (range: CompanyReportRange) =>
    ["company", "reports", "product-mix", range] as const,
  scheduleOps: (range: CompanyReportRange) =>
    ["company", "reports", "schedule-ops", range] as const,
  enrollment: () => ["company", "reports", "enrollment"] as const,
  adoption: (range: CompanyReportRange) =>
    ["company", "reports", "adoption", range] as const,
};

export function useUsageByEmployeeReport(
  range: CompanyReportRange,
  options?: Partial<UseQueryOptions<UsageByEmployeeRow[]>>,
) {
  return useQuery<UsageByEmployeeRow[]>({
    queryKey: companyReportsKeys.usageByEmployee(range),
    queryFn: async () => {
      const url = `${companyEndpoints.reportsUsageByEmployee}${rangeQuery(range)}`;
      return UsageByEmployeeSchema.parse(await apiGet<unknown>(url));
    },
    ...options,
  });
}

export function useSpendByProductReport(
  range: CompanyReportRange,
  options?: Partial<UseQueryOptions<SpendByProductRow[]>>,
) {
  return useQuery<SpendByProductRow[]>({
    queryKey: companyReportsKeys.spendByProduct(range),
    queryFn: async () => {
      const url = `${companyEndpoints.reportsSpendByProduct}${rangeQuery(range)}`;
      return SpendByProductSchema.parse(await apiGet<unknown>(url));
    },
    ...options,
  });
}

export function useMeetingIntelReport(
  range: CompanyReportRange,
  options?: Partial<UseQueryOptions<MeetingIntelReport>>,
) {
  return useQuery<MeetingIntelReport>({
    queryKey: companyReportsKeys.meetingIntel(range),
    queryFn: async () => {
      const url = `${companyEndpoints.reportsMeetingIntel}${rangeQuery(range)}`;
      return MeetingIntelReportSchema.parse(await apiGet<unknown>(url));
    },
    ...options,
  });
}

export function useProductMixReport(
  range: CompanyReportRange,
  options?: Partial<UseQueryOptions<ProductMixRow[]>>,
) {
  return useQuery<ProductMixRow[]>({
    queryKey: companyReportsKeys.productMix(range),
    queryFn: async () => {
      const url = `${companyEndpoints.reportsProductMix}${rangeQuery(range)}`;
      return ProductMixSchema.parse(await apiGet<unknown>(url));
    },
    ...options,
  });
}

export function useScheduleOpsReport(
  range: CompanyReportRange,
  options?: Partial<UseQueryOptions<ScheduleOpsReport>>,
) {
  return useQuery<ScheduleOpsReport>({
    queryKey: companyReportsKeys.scheduleOps(range),
    queryFn: async () => {
      const url = `${companyEndpoints.reportsScheduleOps}${rangeQuery(range)}`;
      return ScheduleOpsReportSchema.parse(await apiGet<unknown>(url));
    },
    ...options,
  });
}

export function useEnrollmentReport(
  options?: Partial<UseQueryOptions<EnrollmentReport>>,
) {
  return useQuery<EnrollmentReport>({
    queryKey: companyReportsKeys.enrollment(),
    queryFn: async () => {
      return EnrollmentReportSchema.parse(
        await apiGet<unknown>(companyEndpoints.reportsEnrollment),
      );
    },
    ...options,
  });
}

export function useAdoptionReport(
  range: CompanyReportRange,
  options?: Partial<UseQueryOptions<AdoptionReport>>,
) {
  return useQuery<AdoptionReport>({
    queryKey: companyReportsKeys.adoption(range),
    queryFn: async () => {
      const url = `${companyEndpoints.reportsAdoption}${rangeQuery(range)}`;
      return AdoptionReportSchema.parse(await apiGet<unknown>(url));
    },
    ...options,
  });
}
